import api from "./api";
import {
  ok, getMe, can, ORG_SCOPES, cycleById, isStageOpen, findRecord, myRecordSummary, recordDetail, recordSection, listRecords,
  splitItems, totalWeight, sheetStatusOf, employeeFromRecord, getLookupsCached, toTimeline, resolveActorNames, cachedItem,
  PRIORITY_TO_API, utcToday, reasonOf, pMap, isoUtc, notImplemented, fileToBase64, evidenceMime, nameOf,
  RESULT_STATES, selfStatusOf, managerStatusOf, extensionsFromHistory, extensionFor, encodeGoalExtras,
  componentWeightIssues, listCyclesCached, pMapSettled,
} from "./core";
import { MAX_GOALS_PER_YEAR, goalLimitMessage } from "../utils/constants";

const SELF_SCOPES = ["SELF", ...ORG_SCOPES];

const yearOf = (cycle) => String(cycle?.startDate || "").slice(0, 4);

export function goalYearLabel(cycle) {
  const start = yearOf(cycle);
  const end = String(cycle?.endDate || "").slice(0, 4);
  return start && end && end !== start ? `${start}-${end.slice(2)}` : start;
}

const activeGoalCount = (record) =>
  (record?.items || []).filter((i) => i.item_type === "GOAL" && i.item_status !== "CANCELLED").length;

export async function getYearGoalUsage(cycleId, employeeId, currentRecord = null) {
  const cycle = await cycleById(cycleId);
  const year = yearOf(cycle);
  const all = await listCyclesCached().catch(() => []);
  const others = all.filter((c) => c.id !== String(cycleId) && year && yearOf(c) === year);
  const current = currentRecord
    ? activeGoalCount(currentRecord)
    : activeGoalCount(await recordDetail((await findRecord(cycleId, employeeId)).performance_record_id));
  const otherCounts = await pMapSettled(others, async (c) => {
    const row = (await listRecords(c.id)).find((r) => r.employee_id === employeeId);
    return row ? activeGoalCount(await recordDetail(row.performance_record_id)) : 0;
  }, 3, 0);
  const used = current + otherCounts.reduce((a, b) => a + (Number(b) || 0), 0);
  return {
    year,
    yearLabel: goalYearLabel(cycle),
    used,
    max: MAX_GOALS_PER_YEAR,
    remaining: Math.max(0, MAX_GOALS_PER_YEAR - used),
    limitReached: used >= MAX_GOALS_PER_YEAR,
  };
}

export async function ensureGoalSlots(cycleId, employeeId, adding = 1) {
  const usage = await getYearGoalUsage(cycleId, employeeId);
  if (usage.used + adding > MAX_GOALS_PER_YEAR) {
    const err = new Error(
      usage.limitReached
        ? goalLimitMessage(usage.yearLabel)
        : `Only ${usage.remaining} more goal${usage.remaining === 1 ? "" : "s"} can be added for the year ${usage.yearLabel} (maximum ${MAX_GOALS_PER_YEAR} goals per year, ${usage.used} already added).`
    );
    err.code = "GOAL_LIMIT_REACHED";
    throw err;
  }
  return usage;
}

async function sheetFor(recordSummary, { forManager = false } = {}) {
  const [record, lookups, me] = await Promise.all([recordDetail(recordSummary.performance_record_id), getLookupsCached(), getMe()]);
  const cycle = await cycleById(record.cycle_id);
  const { goals } = splitItems(record);
  const history = await recordSection(record.performance_record_id, "history", []);
  await resolveActorNames(history, [record.review_manager_employee_id]); // [NEW] manager name in approval history
  const sheetStatus = sheetStatusOf(record);
  const extensions = extensionsFromHistory(history);
  const stageOpen = isStageOpen(cycle, "goalSetting", extensions) && record.current_stage === "GOAL_SETTING";
  const planEditable = ["DRAFT", "CHANGES_REQUESTED"].includes(record.goal_plan?.plan_status);
  const isReviewer = record.review_manager_employee_id === me.employee_id && record.employee_id !== me.employee_id;
  const isOwn = record.employee_id === me.employee_id;
  const goalUsage = await getYearGoalUsage(record.cycle_id, record.employee_id, record).catch(() => ({
    year: yearOf(cycle), yearLabel: goalYearLabel(cycle), used: goals.length, max: MAX_GOALS_PER_YEAR,
    remaining: Math.max(0, MAX_GOALS_PER_YEAR - goals.length), limitReached: goals.length >= MAX_GOALS_PER_YEAR,
  }));
  return {
    recordId: record.performance_record_id,
    record,
    employee: employeeFromRecord(record, lookups),
    goals,
    totalWeightage: totalWeight(goals),
    goalUsage,
    sheetStatus,
    stageOpen,
    extension: extensionFor(cycle, "goalSetting", extensions),
    weightIssues: componentWeightIssues(record),
    meId: me.employee_id,
    canSelfCreate: isOwn && can(me, "goal.self.create", SELF_SCOPES) && stageOpen && planEditable,
    // [UPDATED] Requirement 5: goals are created by the employee only; managers never assign goals.
    canTeamAssign: false,
    hasTeamAssignPermission: false,
    isReviewer,
    planEditable,
    trackingOpen: record.current_stage === "TRACKING",
    currentStage: record.current_stage,
    selfStatus: selfStatusOf(record),
    managerStatus: managerStatusOf(record),
    resultFinalized: Boolean(record.current_result_revision_id) || RESULT_STATES.includes(record.record_status),
    history: toTimeline(history, record.review_manager_employee_id),
    canAct: forManager && record.review_manager_employee_id === me.employee_id && record.goal_plan?.plan_status === "SUBMITTED",
    plan: record.goal_plan,
  };
}

export async function getMyGoalSheet(cycleId) {
  const summary = await myRecordSummary(cycleId);
  return ok(await sheetFor(summary));
}

export async function getEmployeeGoalSheet(cycleId, employeeId) {
  const summary = await findRecord(cycleId, employeeId);
  return ok(await sheetFor(summary, { forManager: true }));
}

function goalBody(form, reason) {
  const target = Number(String(form.target ?? "").replace(/[^0-9.]/g, ""));
  if (!target || target <= 0) throw new Error("Target must be a number greater than 0 (for example 100).");
  const pct = Number(form.weightage);
  if (!pct || pct <= 0 || pct > 100) throw new Error("Goal percentage must be between 1 and 100.");
  return {
    item_title: String(form.title || "").trim(),
    item_description: encodeGoalExtras(form.description, { baseline: form.baseline, source: form.source, notes: form.employeeComments }),
    weight: Number(form.weightage),
    target,
    measurement_unit: form.unit || "%",
    measurement_direction: "HIGHER_IS_BETTER",
    due_date: form.dueDate,
    category: form.categoryName || form.categoryId || null,
    priority: PRIORITY_TO_API[form.priority] || "MEDIUM",
    reason,
  };
}

export async function createGoal(payload) {
  const summary = payload.employeeId ? await findRecord(payload.cycleId, payload.employeeId) : await myRecordSummary(payload.cycleId);
  await ensureGoalSlots(payload.cycleId, summary.employee_id);
  const { data } = await api.post(`/records/${summary.performance_record_id}/goals`, goalBody(payload, reasonOf(payload.employeeComments, "Goal added")));
  return ok(data);
}

// [REMOVED] Requirement 5: assignGoalToTeamMember (managers assigning goals to team members).

export async function updateGoal(goalId, payload) {
  const cached = cachedItem(goalId);
  const version = payload.version ?? cached?.item.version_no;
  const body = { ...goalBody(payload, reasonOf(payload.employeeComments, "Goal updated")), expected_version: version };
  const { data } = await api.put(`/items/${goalId}`, body);
  return ok(data);
}

// [NEW] PM-15 / PM-17 (REACT_APP_BACKEND_REVIEWER_GOAL_EDIT=true): the reviewing manager (while the sheet waits for
// approval) or HR (permission goal.hr.update, before the sheet is approved) edits an employee's goal directly.
// Same PUT /items/{id} as the employee, with a required reason. The backend keeps the change in the history.
export async function reviewerEditGoal(goal, form, reason) {
  const text = String(reason || "").trim();
  if (!text) throw new Error("Please give a reason for the change. The employee sees it in the goal history.");
  const body = {
    ...goalBody(form, text),
    expected_version: goal.version ?? cachedItem(goal.id)?.item.version_no,
  };
  const { data } = await api.put(`/items/${goal.id}`, body);
  return ok(data);
}

export const deleteGoal = (goalId, reason = "Goal removed") => api.delete(`/items/${goalId}`, { data: { reason } });

const weightBlock = (issues) => new Error(`${issues.join(" ")} Ask HR to fix the KRA/KPI weights first (HR → KRA & KPI assignment); otherwise the final score can't be calculated at the end of the review.`);

export async function submitMyGoalSheet(cycleId, reason = "Goals submitted for approval") {
  const summary = await myRecordSummary(cycleId);
  const issues = componentWeightIssues(await recordDetail(summary.performance_record_id));
  if (issues.length) throw weightBlock(issues);
  return api.post(`/records/${summary.performance_record_id}/goals/submit`, { reason });
}

const SEND_MARK_COMPLETED = String(process.env.REACT_APP_PROGRESS_MARK_COMPLETED || "true").toLowerCase() !== "false";

export async function updateGoalProgress(goalId, payload) {
  const pct = payload.markCompleted ? 100 : payload.achievement;
  const body = {
    as_of_date: utcToday(),
    progress_pct: pct === null || pct === undefined ? null : Number(pct),
    comment: payload.employeeComments || "",
    reason: reasonOf(payload.employeeComments, payload.markCompleted ? "Goal completed" : "Progress updated"),
  };
  if (SEND_MARK_COMPLETED) body.mark_completed = Boolean(payload.markCompleted);
  const { data } = await api.post(`/items/${goalId}/progress`, body);
  if (payload.evidenceFile) {
    const cached = cachedItem(goalId);
    const mime = evidenceMime(payload.evidenceFile);
    if (!mime) throw new Error("Progress saved, but evidence must be a PDF, PNG, JPEG or text file.");
    await api.post(`/evidence/performance/${cached?.record.performance_record_id}`, {
      filename: payload.evidenceFile.name,
      mime_type: mime,
      content_base64: await fileToBase64(payload.evidenceFile),
      item_id: Number(goalId),
      context: cached?.item.item_type || "GOAL",
      reason: "Evidence attached to progress update",
    });
  }
  if (payload.evidenceUrl) {
    const cached = cachedItem(goalId);
    try {
      await api.post(`/evidence/performance/${cached?.record.performance_record_id}/links`, {
        url: payload.evidenceUrl,
        title: String(payload.evidenceUrlTitle || cached?.item.item_title || "Progress evidence").slice(0, 200),
        description: String(payload.employeeComments || "").slice(0, 1000),
        item_id: Number(goalId),
        context: cached?.item.item_type || "GOAL",
        reason: "Evidence link attached to progress update",
      });
    } catch (err) {
      const message = err?.response?.data?.error?.message || err.message;
      throw new Error(`Progress saved, but the evidence link was not added: ${message}`);
    }
  }
  return ok(data);
}

export async function getGoalProgressHistory(goalId) {
  const { data } = await api.get(`/items/${goalId}/progress`);
  return ok([...data].reverse().map((p) => ({
    id: p.progress_id,
    date: p.as_of_date,
    value: p.progress_pct ?? p.actual,
    completed: Boolean(p.mark_completed),
    comment: p.comment,
    by: nameOf(p.recorded_by_employee_id),
    at: isoUtc(p.recorded_at || p.created_at),
    supersedes: p.supersedes_progress_id,
    approvalStatus: p.approval_status || "NOT_REQUIRED",
    approvalComment: p.approval_comment || "",
  })));
}

export async function getTeamGoalSheets(cycleId) {
  const me = await getMe();
  const lookups = await getLookupsCached();
  const rows = (await listRecords(cycleId)).filter((r) => r.review_manager_employee_id === me.employee_id && r.employee_id !== me.employee_id);
  const details = await pMap(rows, (r) => recordDetail(r.performance_record_id), 6);
  return ok(details.map((record) => {
    const { goals } = splitItems(record);
    const plan = record.goal_plan || {};
    const lastAction = plan.approved_at
      ? { action: "Approved", at: isoUtc(plan.approved_at) }
      : plan.plan_status === "CHANGES_REQUESTED"
        ? { action: "Changes requested", at: isoUtc(plan.submitted_at) }
        : plan.submitted_at ? { action: "Submitted", at: isoUtc(plan.submitted_at) } : null;
    return {
      recordId: record.performance_record_id,
      employee: employeeFromRecord(record, lookups),
      goalCount: goals.length,
      goalLimitReached: goals.length >= MAX_GOALS_PER_YEAR,
      totalWeightage: totalWeight(goals),
      sheetStatus: sheetStatusOf(record),
      lastAction,
    };
  }));
}

function decisionReason({ comment, goalComments = {} }, fallback, goals = []) {
  const lines = Object.entries(goalComments)
    .filter(([, text]) => String(text || "").trim())
    .map(([id, text]) => `• ${goals.find((g) => g.id === String(id))?.title || `Goal ${id}`}: ${String(text).trim()}`);
  return reasonOf([String(comment || "").trim(), ...lines].filter(Boolean).join("\n"), fallback);
}

export async function approveGoalSheet(cycleId, employeeId, payload = {}) {
  const summary = await findRecord(cycleId, employeeId);
  const record = await recordDetail(summary.performance_record_id);
  const issues = componentWeightIssues(record);
  if (issues.length) throw weightBlock(issues);
  const goals = splitItems(record).goals;
  return api.post(`/records/${summary.performance_record_id}/goals/decision`, {
    decision: "APPROVE",
    reason: decisionReason(payload, "Goals approved", goals),
  });
}

export async function requestGoalChanges(cycleId, employeeId, payload = {}) {
  const summary = await findRecord(cycleId, employeeId);
  const goals = splitItems(await recordDetail(summary.performance_record_id)).goals;
  return api.post(`/records/${summary.performance_record_id}/goals/decision`, {
    decision: "RETURN",
    reason: decisionReason(payload, "Please revise your goals", goals),
  });
}

export async function requestGoalChange(goalId, form, reason) {
  const cached = cachedItem(goalId);
  if (!cached) throw new Error("Reload the page and try again.");
  const body = { ...goalBody(form, reasonOf(reason, "Goal change requested")), expected_version: cached.item.version_no };
  return api.post(`/items/${goalId}/change-requests`, body);
}

export async function getGoalChangeRequests(recordId) {
  const rows = await recordSection(recordId, "change_requests", []);
  return ok(rows.map((c) => ({
    id: c.change_request_id,
    goalId: String(c.performance_item_id),
    goalTitle: cachedItem(c.performance_item_id)?.item.item_title || `Goal ${c.performance_item_id}`,
    itemType: cachedItem(c.performance_item_id)?.item.item_type || "GOAL",
    status: c.request_status,
    reason: c.request_reason,
    decisionReason: c.decision_reason,
    proposed: c.proposed_changes || {},
    requestedBy: nameOf(c.requested_by_employee_id),
    requestedAt: isoUtc(c.requested_at),
  })));
}

export const decideGoalChange = (changeId, approve, reason, { itemType = "GOAL" } = {}) => {
  if (approve && itemType !== "GOAL") {
    return Promise.reject(new Error("This is a KRA/KPI target set by HR. Reject the request and ask HR to change it (HR → KRA & KPI assignment)."));
  }
  return api.post(`/goal-change-requests/${changeId}/decision`, { approve, reason: reasonOf(reason, approve ? "Change approved" : "Change rejected") });
};

export async function reopenGoalSheet() {
  throw notImplemented("Reopening an approved goal sheet is not supported by the backend yet. The employee can raise a change request on a goal instead.");
}

export async function getMyGoalSuggestions(cycleId) {
  try {
    const me = await getMe();
    if (!can(me, "master.configure", ORG_SCOPES)) return ok(null);
    const summary = await myRecordSummary(cycleId);
    const record = await recordDetail(summary.performance_record_id);
    const designationId = record.context_snapshot?.designation_id;
    const { data: templates } = await api.get("/configuration/performance_template");
    const template = templates
      .filter((t) => t.status === "APPROVED" && t.template_code.startsWith(`ROLE-D${designationId}`))
      .sort((a, b) => b.version_no - a.version_no)[0];
    if (!template) return ok(null);
    return ok({
      templateId: template.template_id,
      designationName: record.context_snapshot?.designation_name,
      goals: template.items.filter((x) => x.item_type === "KRA").map((x, i) => {
        const snap = x.definition_snapshot || {};
        return {
          key: `${x.item_type}-${i}`,
          title: snap.kra_name || snap.kpi_name || `Template item ${i + 1}`,
          description: snap.description || "",
          categoryId: "Business",
          categoryName: "Business",
          priority: "Medium",
          weightage: Number(x.weight),
          baseline: "",
          target: Number(x.target),
          unit: snap.measurement_unit || "%",
          source: "Role template",
        };
      }),
    });
  } catch {
    return ok(null);
  }
}